import { Parser as N3Parser, type Quad } from "n3";
import type { DatasetFile } from "./types";

const N3_FORMAT_BY_FILE_FORMAT: Record<DatasetFile["format"], string> = {
  turtle: "Turtle",
  ntriples: "N-Triples",
  trig: "TriG",
  nquads: "N-Quads",
};

export interface ParseResult {
  quads: Quad[];
  /** Prefixes this file declared (`@prefix p: <...> .`), keyed by prefix. */
  prefixes: Record<string, string>;
}

/**
 * Parses one RDF source file's text into quads plus its declared prefixes.
 * Synchronous (N3.Parser.parse returns an array directly when no quad
 * callback is passed) — fine at Milestone 1's file sizes (single-digit MB at
 * most); would need to switch to the streaming callback form if Milestone
 * 2's user-uploaded files turn out to be large enough to block the main
 * thread noticeably.
 */
export function parseRdfFile(text: string, file: DatasetFile): ParseResult {
  const prefixes: Record<string, string> = {};
  const parser = new N3Parser({
    format: N3_FORMAT_BY_FILE_FORMAT[file.format],
  });

  let quads: Quad[];
  try {
    quads = parser.parse(text, null, (prefix, prefixNode) => {
      prefixes[prefix] = prefixNode.value;
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`Failed to parse ${file.path} (${file.format}): ${message}`);
  }

  return { quads, prefixes };
}
